<template>
  <Modal :is-open="isOpen" @close="$emit('close')">
    <template #title>{{ customer?.attributes?.display_name }} orders</template>
    <template #body>
      <div v-if="loading" class="flex justify-center py-10"><BasicSpinner /></div>
      <p v-else-if="!bottles.length" class="text-gray-brand-500 py-6 text-center">No orders yet.</p>
      <ul v-else class="divide-y">
        <li v-for="bottle in bottles" :key="bottle.id" class="py-3 flex items-center justify-between gap-4">
          <div class="min-w-0">
            <p class="font-semibold text-sm">Order #{{ bottle.attributes.display_id || bottle.id }}</p>
            <p class="text-xs text-gray-brand-500">
              {{ formatDate(bottle.attributes.created_at) }}
              <span :class="orderStatus(bottle).class" class="font-semibold ml-1">
                {{ orderStatus(bottle).label }}
              </span>
            </p>
            <button
              v-if="bottle.attributes.link"
              class="text-xs font-semibold underline text-gray-brand-500 hover:text-black mt-0.5"
              @click="copyLink(bottle)"
            >
              {{ copiedId === bottle.id ? 'Copied!' : 'Copy customer link' }}
            </button>
          </div>
          <div class="flex items-center gap-4 shrink-0">
            <span v-if="bottleTotal(bottle)" class="text-sm font-semibold">{{ bottleTotal(bottle) }}</span>
            <PrimaryButton
              v-if="bottle.attributes.payment_status !== 'paid'"
              size="md"
              :isLoading="continuingId === bottle.id"
              @click="continueOrder(bottle)"
            >
              Continue
            </PrimaryButton>
          </div>
        </li>
      </ul>
      <p v-if="continueError" class="text-sm text-red-600 mt-3">{{ continueError }}</p>
    </template>
  </Modal>
</template>

<script>
import Modal from '@/components/Molecules/Modal'
import PrimaryButton from '@/components/Molecules/PrimaryButton'
import BasicSpinner from '@/components/Molecules/BasicSpinner'
import merchantFetch from '@/utils/merchantFetch'

export default {
  name: 'CustomerOrdersModal',
  components: { Modal, PrimaryButton, BasicSpinner },
  props: {
    isOpen: { type: Boolean, default: false },
    customer: { type: Object, default: null }
  },
  emits: ['close'],
  data() {
    return {
      loading: false,
      bottles: [],
      checkoutsById: {},
      paymentsById: {},
      continuingId: null,
      continueError: null,
      copiedId: null
    }
  },
  watch: {
    isOpen(open) {
      if (open && this.customer) {
        this.continueError = null
        this.load()
      }
    }
  },
  methods: {
    async load() {
      this.loading = true
      this.bottles = []
      this.checkoutsById = {}
      this.paymentsById = {}
      try {
        const res = await merchantFetch(`reseller/customers/${this.customer.id}/bottles`, {
          params: { included: 'checkout,bottle_payment' }
        })
        this.bottles = res.data || []
        this.checkoutsById = this.includedById(res, 'checkout')
        this.paymentsById = this.includedById(res, 'bottle_payment')
      } finally {
        this.loading = false
      }
    },
    includedById(res, type) {
      return Object.fromEntries((res.included || []).filter((x) => x.type === type).map((x) => [x.id, x]))
    },
    // "Paid" only when the merchant marked the order as paid; an order settled
    // through checkout (card or 100% code) is "Order Placed".
    orderStatus(bottle) {
      if (bottle.attributes.payment_status !== 'paid') return { label: 'Unplaced', class: 'text-amber-700' }
      const payment = this.paymentsById[bottle.relationships?.bottle_payment?.data?.id]
      return payment?.attributes?.reason === 'mark_as_paid'
        ? { label: 'Paid', class: 'text-green-700' }
        : { label: 'Order Placed', class: 'text-blue-700' }
    },
    bottleTotal(bottle) {
      const checkoutId = bottle.relationships?.checkout?.data?.id
      return this.checkoutsById[checkoutId]?.attributes?.bottle_total?.format ?? ''
    },
    formatDate(iso) {
      if (!iso) return ''
      return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    },
    async copyLink(bottle) {
      await navigator.clipboard.writeText(bottle.attributes.link)
      this.copiedId = bottle.id
      setTimeout(() => (this.copiedId = null), 2000)
    },
    async continueOrder(bottle) {
      if (this.continuingId) return
      this.continueError = null
      this.continuingId = bottle.id
      try {
        const res = await merchantFetch('reseller/impersonation_tokens', {
          method: 'POST',
          body: JSON.stringify({ conversation_user_pair_id: this.customer.id })
        })
        this.$store.dispatch('startResellerImpersonation', { token: res.token })
        this.$emit('close')
        this.$router.push({ name: 'Selection', params: { bottleid: bottle.id } })
      } catch (e) {
        this.continueError = 'Could not continue this order. Please try again.'
      } finally {
        this.continuingId = null
      }
    }
  }
}
</script>
